const isValidTokenRole = require('../isValidTokenRole');
const appendQueryParamToUrl = require('../append-query-param-to-url');
const {
  getCustomSessionEnvironment,
  getSessionStorage,
  storeCustomSession,
  clearStoredCustomSession,
} = require('../custom-session');
const {
  CAPTIONS_LANGUAGES,
  CAPTIONS_INPUT_LANGUAGE_KEY,
  CAPTIONS_TRANSLATION_LANGUAGE_KEY,
  CAPTIONS_INPUT_LANGUAGE_PARAM,
  CAPTIONS_TRANSLATION_LANGUAGE_PARAM,
  isSupportedLanguage,
  captionsLanguageApiAvailable,
} = require('../captions-languages');

const CUSTOM_ROOM_NAME = 'custom';
const isp2p = room => room && room.toLowerCase().indexOf('p2p') > -1;

angular.module('opentok-meet-login', [])
  .controller('MainCtrl', ['$scope', '$window', function MainCtrl($scope, $window) {
    $scope.room = '';
    $scope.roomType = 'normal';
    $scope.tokenRole = 'moderator';
    $scope.advanced = false;
    $scope.dtx = true;
    $scope.paf = true;
    $scope.audioOnlyMode = false;
    $scope.e2ee = false;
    $scope.encryptionSecret = '';
    $scope.sessionMigration = true;
    $scope.initials = '';
    $scope.ans = true;
    $scope.opentokVersion = '';
    $scope.showOpentokVersion = false;
    $scope.useCustomSession = false;
    $scope.customSessionEnvironment = 'development';
    $scope.customSessionEnvironments = [
      { value: 'rel', label: 'Rel' },
      { value: 'production', label: 'Production' },
      { value: 'development', label: 'Development' },
    ];
    $scope.customApiKey = '';
    $scope.customSessionId = '';
    $scope.customToken = '';

    // Captions language selection. Only surfaced when the loaded SDK supports the
    // captions-language APIs (publisher.setCaptionsInputLanguage /
    // subscriber.setCaptionsTranslationLanguage). Choices are remembered in
    // localStorage and forwarded to the room page as query params.
    $scope.captionsLanguageSupported = captionsLanguageApiAvailable($window.OT);
    $scope.captionsLanguages = CAPTIONS_LANGUAGES;
    // Use an object (dotted model) so the ng-model bindings inside the ng-if child
    // scope write through to this same object rather than shadowing a primitive on a
    // child scope. Without this, selections made in the ng-if block never reach here.
    $scope.captions = {
      input: '',
      translation: '',
    };

    if ($scope.captionsLanguageSupported) {
      const storedInput = $window.localStorage.getItem(CAPTIONS_INPUT_LANGUAGE_KEY);
      const storedTranslation = $window.localStorage.getItem(CAPTIONS_TRANSLATION_LANGUAGE_KEY);
      if (isSupportedLanguage(storedInput)) {
        $scope.captions.input = storedInput;
      }
      if (isSupportedLanguage(storedTranslation)) {
        $scope.captions.translation = storedTranslation;
      }

      const persist = (key, value) => {
        if (isSupportedLanguage(value)) {
          $window.localStorage.setItem(key, value);
        } else {
          $window.localStorage.removeItem(key);
        }
      };
      $scope.$watch('captions.input', (value) => {
        persist(CAPTIONS_INPUT_LANGUAGE_KEY, value);
      });
      $scope.$watch('captions.translation', (value) => {
        persist(CAPTIONS_TRANSLATION_LANGUAGE_KEY, value);
      });
    }

    $scope.customSessionChanged = () => {
      $scope.customSessionError = '';
      if ($scope.useCustomSession && (!$scope.room || !$scope.room.trim())) {
        $scope.room = CUSTOM_ROOM_NAME;
      }
    };

    $scope.joinRoom = () => {
      $scope.customSessionError = '';
      if ($scope.useCustomSession &&
        (!$scope.customApiKey || !$scope.customApiKey.trim()
          || !$scope.customSessionId || !$scope.customSessionId.trim()
          || !$scope.customToken || !$scope.customToken.trim())) {
        $scope.customSessionError = 'API key, session ID, and token are all required.';
        return;
      }

      if (!$scope.useCustomSession) {
        clearStoredCustomSession(getSessionStorage($window));
      }

      const location = new URL($window.location.href);
      const room = $scope.useCustomSession && (!$scope.room || !$scope.room.trim())
        ? CUSTOM_ROOM_NAME
        : $scope.room;
      let url = `${location.origin}/${encodeURIComponent(room)}`;

      if ($scope.roomType !== 'normal') {
        url += `/${$scope.roomType}`;
      }

      if (!isValidTokenRole($scope.tokenRole)) {
        $scope.tokenRole = 'moderator';
      }

      if ($scope.tokenRole) {
        url = appendQueryParamToUrl(`tokenRole=${$scope.tokenRole}`, url);
      }

      if (!$scope.dtx) {
        url = appendQueryParamToUrl('dtx=false', url);
      }

      if (!$scope.paf) {
        url = appendQueryParamToUrl('paf=false', url);
      }

      if ($scope.audioOnlyMode) {
        url = appendQueryParamToUrl('audioOnlyMode=true', url);
      }

      if ($scope.e2ee && $scope.encryptionSecret) {
        url = appendQueryParamToUrl(`e2ee=${$scope.encryptionSecret}`, url);
      }

      if ($scope.initials) {
        url = appendQueryParamToUrl(`initials=${$scope.initials}`, url);
      }

      if (!$scope.ans) {
        url = appendQueryParamToUrl('ans=false', url);
      }
      if (!$scope.sessionMigration) {
        url = appendQueryParamToUrl('sessionMigration=false', url);
      }

      if ($scope.showOpentokVersion && $scope.opentokVersion && $scope.opentokVersion.trim()) {
        const version = encodeURIComponent($scope.opentokVersion.trim());
        url = appendQueryParamToUrl(`otVersion=${version}`, url);
      }

      if ($scope.captionsLanguageSupported) {
        if (isSupportedLanguage($scope.captions.input)) {
          const inputParam = `${CAPTIONS_INPUT_LANGUAGE_PARAM}=${$scope.captions.input}`;
          url = appendQueryParamToUrl(inputParam, url);
        }
        if (isSupportedLanguage($scope.captions.translation)) {
          const translationParam =
            `${CAPTIONS_TRANSLATION_LANGUAGE_PARAM}=${$scope.captions.translation}`;
          url = appendQueryParamToUrl(translationParam, url);
        }
      }

      if ($scope.useCustomSession) {
        const environment = getCustomSessionEnvironment($scope.customSessionEnvironment)
          ? $scope.customSessionEnvironment
          : 'development';
        url = appendQueryParamToUrl(
          `customSessionEnvironment=${encodeURIComponent(environment)}`,
          url,
        );
        const customSession = {
          apiKey: $scope.customApiKey.trim(),
          sessionId: $scope.customSessionId.trim(),
          token: $scope.customToken.trim(),
        };
        if (!storeCustomSession(getSessionStorage($window), customSession)) {
          $scope.customSessionError = 'Unable to securely store the custom session in this browser.';
          return;
        }
      }

      $window.location.href = url;
    };
    $scope.p2p = false;
    $scope.$watch('room', (room) => {
      $scope.p2p = isp2p(room);
    });
    $scope.p2pChanged = () => {
      if ($scope.p2p && !isp2p($scope.room)) {
        $scope.room += 'p2p';
      } else if (!$scope.p2p) {
        $scope.room = $scope.room.replace('p2p', '');
      }
    };
  }]);
